import moment from 'moment';
import { capitalizeFirstLetter } from './api';
import { formatImportDate } from './dates';

/**
 * color for the status of the import
 *  @param {string} status - the status of the import
 * @returns {string} - the color class for the status
 *
*/

export const statusAndFileFontColor = (status) => {
  switch (status) {
    case 'pending':
      return 'text-vamos-gray';
    case 'error':
      return 'text-vamos-red';
    case 'complete':
      return 'text-vamos-green4';
    default:
      return '';
  }
};

/**
 * text color for columns with filename and status, depending on the status
 *  @param {object} column - the column object
 *  @param {object} data - the data object
 * @returns {string} - the color class for the status
 *
*/
export const columnTextColor = ({ column, data }) => {
  return ['status', 'filename'].includes(column.dataIndex) ? statusAndFileFontColor(data.status) : undefined;
};

/**
 * format the status of the import and add '...' if it is pending
 * @param {string} status - the status of the import
 * @returns {string} - the formatted status
 */
const formatStatus = (status) => {
  return capitalizeFirstLetter(status === 'pending' ? status.concat('...') : status);
};

/**
 * add plural form for the type of import if it is not plural
 * @param {string} type - the type of import
 * @returns {string} - the formatted type
 *
*/
const formatType = (type) => {
  return capitalizeFirstLetter(type.at(-1) === 's' ? type : type.concat('s'));
};

/**
 * the columns for the imports table
 * @returns {array} - the columns for the imports table
 */

export const columns = [
  {
    title: 'Date', dataIndex: 'created_at', isSortable: false,
  },
  {
    title: 'Name',
    dataIndex: 'filename',
    isSortable: false,
  },
  {
    title: 'Type', dataIndex: 'import_type', isSortable: false, render: (data) => formatType(data),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    isSortable: false,
    render: (data) => formatStatus(data),
  },
  {
    title: 'User',
    dataIndex: 'user',
    isSortable: false,
  },
  { title: 'Delete', dataIndex: 'action', isSortable: false },
];

/**
 * the options for the import select
 * @returns {array} - the options for the import select
 */

export const importOptions = [
  {
    value: 'sites',
    label: 'Sites',
    type: 'site',
    helpText: 'Download the template and add your data. Review the Site terms for formatting guidance. Export as CSV. Name your file descriptively (e.g., "2022-2025 camp data").',
    templateUrl: 'https://docs.google.com/spreadsheets/d/1Ey7tqVQalS7rZbnLfs6sD4YKe3b9Ejwz11v8Xw9Nfss',
    termUrl: 'https://docs.google.com/document/d/1wJijOJokFYzAdikGchZh1rD_qg3Wb2Yibpz8Aju8Zpg/edit?tab=t.0#heading=h.n2umzmo08l0k',
  },
  {
    value: 'visits',
    label: 'Visits',
    type: 'visit',
    helpText: 'Download the template and add your data. Review the Visit terms for formatting guidance. Export as CSV. Name your file descriptively (e.g., "2022-2025 camp visits").',
    templateUrl: 'https://docs.google.com/spreadsheets/d/14JLOEVeqj-QIXNraLtn1a8_CK32T0-7sLKFSDlldO9U',
    termUrl: 'https://docs.google.com/document/d/1wJijOJokFYzAdikGchZh1rD_qg3Wb2Yibpz8Aju8Zpg/edit?tab=t.0#heading=h.nbgkxvsbpp08',
  },
  {
    value: 'contacts',
    label: 'Contacts',
    type: 'contact',
    helpText: 'Download the template and add your data. Review the Contact terms for formatting guidance. Export as CSV. Name your file descriptively (e.g., "2022-2025 contacts").',
    templateUrl: 'https://docs.google.com/spreadsheets/d/1dZ7gYwr_1cFhSXoVLkc7ZNbIs8f_UZ6sg-QxUScwcYY',
    termUrl: 'https://docs.google.com/document/d/1wJijOJokFYzAdikGchZh1rD_qg3Wb2Yibpz8Aju8Zpg/edit?tab=t.0#heading=h.dtzx4nio0kac',
  },
  {
    value: 'notes',
    label: 'Notes',
    type: 'note',
    helpText: 'Download the template and add your data. Review the Note terms for formatting guidance. Export as CSV. Name your file descriptively (e.g., "2022-2025 camp notes").',
    templateUrl: 'https://docs.google.com/spreadsheets/d/12l_-QMAZ-qUbEv-L88dwTcs42MyJE4UMecSHtDsJ7cM',
    termUrl: 'https://docs.google.com/document/d/1wJijOJokFYzAdikGchZh1rD_qg3Wb2Yibpz8Aju8Zpg/edit?tab=t.0#heading=h.5am5r922fup',
  },
];

/**
 * @param {string} type - the type of import
 * @returns {Array<string>} - the fields for the import
 * @description - this function will return the fields for the import based on the type
 */

export const fieldNames = (type) => {
  switch (type) {
    case 'contact':
      return ['name', 'phone', 'company', 'note', 'contact_type', 'email', 'address1', 'address2', 'city', 'state', 'zip_code'];
    case 'note':
      return ['title', 'content', 'timestamp', 'status', 'priority', 'site_name', 'user_name'];
    case 'visit':
      return ['site_name', 'date', 'purpose', 'user_name', 'leader', 'visitors', 'total_workers', 'workers_interacted', 'needs_concerns', 'other_notes', 'completed'];
    default:
      return ['name', 'alternate_ids', 'alternate_names', 'description', 'site_type', 'units', 'worker_count', 'arrival', 'departure', 'h2a', 'address1', 'address2', 'city', 'county', 'state', 'zip_code', 'region', 'coords_set_manually', 'latitude', 'longitude', 'directions', 'memo', 'status', 'icon', 'contact_name'];
  }
};
/**
 * @param {string} fieldName - the name of the field
 * @param {string} type - the type of import
 * @returns {string} - the formatted field name
 * @description - this function will return the formatted field name based on the field name
 */

export const formatFieldName = (fieldName, type) => {
  switch (fieldName) {
    case 'alternate_ids':
      return 'Alternate IDs';
    case 'address1':
      return 'Address line 1';
    case 'address2':
      return 'Address line 2';
    case 'zip_code':
      return 'ZIP code';
    case 'h2a':
      return 'H2A';
    case 'contact_name':
      return 'Primary contact';
    case 'completed':
      return 'Visit status';
    case 'coords_set_manually':
      return 'Coords set manually?';
    case 'needs_concerns':
      return 'Needs/Concerns';
    case 'date':
      return capitalizeFirstLetter(type).concat(' date');
    default:
      return capitalizeFirstLetter(fieldName).replace(/_/g, ' ');
  }
};

/**
 * @param {string} fieldName - the name of the field
 * @param {string} value - the value of the field
 * @returns {string} - the formatted field value
 * @description - this function will return the formatted field value based on the field name
 */
export const formatFieldValue = (fieldName, value) => {
  switch (fieldName) {
    case 'completed':
      return value ? 'Completed' : 'Attempted';
    case 'h2a':
      return value ? 'Yes' : 'No';
    case 'priority':
      return value === 'no_priority' ? 'no priority' : value;
    case 'coords_set_manually':
      return value ? 'Yes' : 'No';
    case 'arrival':
    case 'departure':
    case 'timestamp':
    case 'date':
      return value ? formatImportDate(value) : 'No data';
    default:
      return value || 'No data';
  }
};

/**
 * Formats a string value as a date if it matches YYYY-MM-DD format (from db)
 * otherwise returns the original value unchanged.
 *
 * @param {string} value - raw field value from the import row
 * @returns {string} formatted date string or the original value
 */
export const formatCustomFieldValue = (value) => {
  return moment(value, 'YYYY-MM-DD', true).isValid() ? formatImportDate(value) : value;
};
